<template>
  <Modal name="damageSpell">
    <template v-slot:default="{ options }">
      <ModalCard>
        <template v-slot:title>Roll Damage for {{ options.item.name }}</template>
        
        <div class="field has-addons has-addons-centered">
          <div class="control">
            <DiceRoll ref="diceRoller" :roll="roll" :character="character" :computed="computed" />
          </div>
        </div>

        <div class="field is-grouped is-grouped-centered">
          <div v-if="options.item.extraDamage && options.item.level > 0" class="control">
            <div class="buttons has-addons">
              <a 
                v-for="(level) in levels" :key="level" 
                :class="{ 'button': true, 'is-primary': selected === level }" 
                :disabled="(level < options.item.level || level > maxPoints) ? true : null" 
                @click="selected = level"
              >
                {{ level }}
              </a>
            </div>
          </div>
          <!-- <div v-if="options.item.saves && options.item.halfOnMiss" class="control">
            <div class="buttons has-addons">
              <a :class="{ 'button': true, 'is-primary': saved === 'full' }" @click="saved = 'full'">Full</a>
              <a :class="{ 'button': true, 'is-primary': saved === 'half' }" @click="saved = 'half'">Half</a>
            </div>
          </div> -->
          <div class="field has-addons has-addons-centered">
            <div v-if="!appState.hardRolls" class="control">
              <a class="button is-primary" @click="rollDice">Roll</a>
            </div>
            <div v-if="bonuses.inspiration" class="control">
              <a :class="{ 'button': true, 'is-primary': useInspiration }" :disabled="options.removeInspiration" @click="useInspiration = !useInspiration">+1</a>
            </div>
          </div>
        </div>

        <template v-slot:buttons>
          <div class="buttons has-addons">
            <button class="button is-primary" @click="closeModal" :disabled="!appState.hardRolls && !outcomeValue">Finish Casting Spell</button>
          </div>
        </template>
      </ModalCard>
    </template>
  </Modal>
</template>

<script>
import { defineComponent, ref, watch, inject } from 'vue'

import useModal from '@/hooks/useModal'

import Modal from '@/components/Modal'
import ModalCard from '@/components/ModalCard'
import DiceRoll from '@/components/DiceRoll'

export default defineComponent({
  components: {
    DiceRoll,
    Modal,
    ModalCard
  },

  setup (props, { emit }) {
    const character = inject('character')
    const computed = inject('computedCharacter')
    const appState = inject('appState')

    const { currentModal } = useModal()

    const outcomeValue = ref(null)
    const diceRoller = ref(null)
    const rollDice = () => {
      if (useInspiration.value && computed.bonuses.value.inspiration) {
        currentModal.value.options.onClose = () => {
          emit('removeInspiration')
        }
      } else {
        delete currentModal.value.options.onClose
      }

      if (appState.hardRolls) {
        outcomeValue.value = Math.abs(Number(prompt('What was the outcome of your roll?') || 0))
      } else {
        outcomeValue.value = diceRoller.value.rollDice().outcome
      }
    }
    watch(currentModal, () => {
      outcomeValue.value = null
    })

    const selected = ref(0)
    const useInspiration = ref(false)
    watch(currentModal, (modal) => {
      if (modal === null || modal.name !== 'damageSpell') {
        useInspiration.value = false
        return
      }

      selected.value = modal.options.item.level
    })

    const saved = ref('full')
    watch(currentModal, (modal) => {
      if (modal === null || modal.name !== 'damageSpell') {
        return saved.value = 'full'
      }

      saved.value = modal.options.saved || 'full'
    })
 
    const levels = ref([])
    watch(currentModal, (modal) => {
      if (modal === null || modal.name !== 'damageSpell') {
        return
      }

      let arr = []
      arr.length = computed.characterClass.value.spellLevel
      arr.fill()
      levels.value = arr.map((_, i) => i + 1)
    })
    
    const maxPoints = ref(0)
    watch(currentModal, (modal) => {
      if (modal === null || modal.name !== 'damageSpell') {
        return
      }

      maxPoints.value = Math.min(character.spellPoints, computed.characterClass.value.spellLevel)
    })

    const roll = ref(null)
    const makeRoll = (modal, inspired, numPoints) => {
      if (modal === null || modal.name !== 'damageSpell') {
        return roll.value = []
      }

      const spell = modal.options.item

      outcomeValue.value = null
      if (diceRoller.value) diceRoller.value.reset()

      const parts =  [ `+${spell.damage}` ]

      const extraPoints = numPoints - spell.level
      if (spell.extraDamage && extraPoints) {
        const die = spell.extraDamage.split('d')
        const numDie = extraPoints * Number(die[0])
        parts.push(`+${numDie}d${die[1]}`)
      }
      
      const additionalDamage = (spell.additionalDamage || 0) + ((spell.additionalExtraDamage || 0) * extraPoints) + computed.bonuses.value.spellDamage
      if (additionalDamage > 0) parts.push(`+${additionalDamage}`)
      else if (additionalDamage < 0) parts.push(`-${additionalDamage}`)

      if (inspired && computed.bonuses.value.inspiration) {
        parts.push('+1')
      }

      roll.value = parts
    }
    watch(selected, (numPoints) => makeRoll(currentModal.value, useInspiration.value, numPoints))
    watch(currentModal, (modal) => makeRoll(modal, useInspiration.value, selected.value))
    watch(useInspiration, (inspired) => makeRoll(currentModal.value, inspired, selected.value))

    const terminateModal = inject('terminateModal')
    const closeModal = () => {
      terminateModal(null, {
        ...currentModal.value.options,
        spellPointsSpent: selected.value
      })
    }
    
    return {
      appState,
      character,
      computed,
      ...computed,
      roll,
      selected,
      saved,
      levels,
      maxPoints,
      closeModal,
      diceRoller,
      rollDice,
      outcomeValue,
      useInspiration
    }
  }
})
</script>
